import React from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  TextField,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import { V4_DIALOG_CONTENT } from '../../theme/v4Compat';
import { notifications, extractApiErrorMessage } from '../../utils';
import Alert from '@mui/material/Alert';
import { MedicalSupplyViewModel } from '../../models/injection';
import { MedicalSupplySelectModal } from '../../elements';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../hooks';

interface IProps {
  opened: boolean;
  onModalAction: (result: boolean) => void;
}

const MedicalSupplyUseExistingModal: React.FC<any> = ({ opened, onModalAction }: IProps) => {
  const styles = sxStyles(useTheme());
  const { t } = useTranslation();
  const apiService = new ApiService();

  const [isBusy, setIsBusy] = React.useState(false);
  const [supply, setSupply] = React.useState(null as MedicalSupplyViewModel | null);
  const [inStock, setInStock] = React.useState(0);

  const validate = (): boolean => {
    if (!supply) {
      notifications.warning(t('f-val-supply-use'));
      return false;
    }
    if (inStock < 0) {
      notifications.warning(t('f-val-instock'));
      return false;
    }
    return true;
  };

  const clearFields = () => {
    setSupply(null);
    setInStock(0);
  };

  const handleAddFarmSupply = () => {
    if (!validate()) {
      return;
    }

    if (!supply) {
      return;
    }

    setIsBusy(true);

    apiService.api.medicalSupplies
      .medicalSuppliesCreateFarmMedicalSupply({
        medicalSupplyId: supply.id,
        inStock: inStock
      })
      .then(() => {
        notifications.success(t('f-supp-farm-add-ok'));
        clearFields();
        onModalAction(true);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => setIsBusy(false));
  };

  return (
    <Dialog
      id="useSupplyDialog"
      open={opened}
      onClose={() => onModalAction(false)}
      sx={styles.dialog}
      maxWidth="sm"
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="useSupplyDialogTitle" sx={styles.dialogHeaderFooter}>
        {t('f-supp-add-exist-title')}
      </DialogTitle>

      <DialogContent sx={V4_DIALOG_CONTENT}>
        <Grid container direction="column" spacing={1} sx={styles.updateForm}>
          <Grid item>
            <Alert sx={styles.infoAlert} severity="info">
              {t('f-supp-add-exist-alert')}
            </Alert>
          </Grid>

          <Grid item>
            <MedicalSupplySelectModal
              disableAny
              supply={supply}
              sx={{ m: 1, minWidth: 220 }}
              onChange={(supply) => setSupply(supply)}
            />
          </Grid>

          <Grid item>
            <TextField
              label={t('f-supp-in-stock')}
              variant="outlined"
              type="number"
              size="small"
              sx={styles.formControl}
              value={inStock}
              onChange={(e) => {
                setInStock(Number(e.target.value));
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={styles.dialogHeaderFooter}>
        {isBusy && <CircularProgress />}

        <Button
          onClick={() => onModalAction(false)}
          variant="outlined"
          disabled={isBusy}
          startIcon={<Icon>{'close'}</Icon>}
          color="primary"
        >
          {t('f-com-cancel')}
        </Button>

        <Button
          onClick={handleAddFarmSupply}
          id="saveBtn"
          disabled={isBusy}
          startIcon={<Icon>{'check_circle_outline'}</Icon>}
          variant="contained"
          color="primary"
        >
          {t('f-com-add-selected')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MedicalSupplyUseExistingModal;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) => {
  const fieldMaxWidth = 220;
  return {
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column'
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: fieldMaxWidth
    },
    dialogHeaderFooter: {
      background: colors.defaultGrey
    },
    dialog: {
      overflow: 'hidden'
    },
    updateForm: {
      minWidth: 250,
      justifyContent: 'center'
    },
    infoAlert: {
      margin: theme.spacing(1),
      maxWidth: fieldMaxWidth - 30
    },
    infoCardAvatar: {
      background: theme.palette.primary.main
    }
  } as const;
};
